/**
 * Runtime configuration loader.
 *
 * Vite's `import.meta.env.VITE_*` values are baked into the JS bundle at
 * BUILD time. For IIS-hosted multi-environment deployment we need a single
 * build artifact that can be dropped into Stage / Production / etc., so we
 * fetch `/config.json` at startup and route everything that depends on the
 * environment through `getRuntimeConfig()`.
 *
 * Editing `dist/config.json` on the IIS server and recycling the site is
 * enough to change the API URL — no rebuild required.
 */

export interface RuntimeConfig {
  /**
   * Absolute origin of the InveTrak API, e.g. "https://portal.invetrak.com:442".
   * NOT terminated with a slash. Request paths like "/v1/registration" are
   * appended verbatim — see `apiRequest` in shared/api/client.ts.
   */
  apiBaseUrl: string;
  /**
   * Free-form environment label shown in diagnostic UI ("stage" | "prod" | …).
   */
  environment: string;
  /**
   * Absolute origin where this PWA itself is served. Used to build links in
   * email templates rendered server-side via a /tenant/branding-ish endpoint
   * (future). Optional in this module so a missing key still works.
   */
  appBaseUrl?: string;
}

let cached: RuntimeConfig | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load /config.json: HTTP ${response.status}`);
  }
  const raw = (await response.json()) as Partial<RuntimeConfig>;
  if (!raw.apiBaseUrl || !raw.environment) {
    throw new Error('/config.json is missing required keys (apiBaseUrl, environment).');
  }
  cached = {
    apiBaseUrl: raw.apiBaseUrl.replace(/\/+$/, ''),
    environment: raw.environment,
    appBaseUrl: raw.appBaseUrl?.replace(/\/+$/, ''),
  };
  return cached;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error(
      'Runtime config not loaded yet. main.tsx must await loadRuntimeConfig() before rendering.',
    );
  }
  return cached;
}
